import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Users, ChevronRight, X, Send, Loader2, CalendarCheck } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const RECAPTCHA_SITE_KEY = '6LeZcZMsAAAAAIX_useKAEYZFfupc9FAWQEYhkxc';
const RESERVATION_HERO = '/images/soursweet-konzert.png';

export const Reservation = () => {
  const { language } = useLanguage();
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const recaptchaWidgetId = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    guests: '',
    message: ''
  });

  const dialogRef = useRef(null);
  const recaptchaContainerRef = useRef(null);
  
  // Refs to store current values for callback
  const formDataRef = useRef(formData);
  const selectedEventRef = useRef(selectedEvent);
  
  // Keep refs in sync with state
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  
  useEffect(() => {
    selectedEventRef.current = selectedEvent;
  }, [selectedEvent]);

  // Submit form function that uses refs
  const submitFormWithToken = useCallback(async (token) => {
    const currentFormData = formDataRef.current;
    const currentSelectedEvent = selectedEventRef.current;
    
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', currentFormData.name);
      formDataToSend.append('email', currentFormData.email);
      formDataToSend.append('phone', currentFormData.phone);
      formDataToSend.append('guests', currentFormData.guests);
      formDataToSend.append('date', currentFormData.date);
      formDataToSend.append('time', currentFormData.time);
      formDataToSend.append('eventType', currentSelectedEvent?.name || 'General');
      formDataToSend.append('message', currentFormData.message);
      formDataToSend.append('g-recaptcha-response', token);
      
      const response = await fetch('/api/send.php', {
        method: 'POST',
        body: formDataToSend
      });
      
      const result = await response.json();
      
      if (result.success) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', date: '', time: '', guests: '', message: '' });
        if (recaptchaWidgetId.current !== null && window.grecaptcha) {
          window.grecaptcha.reset(recaptchaWidgetId.current);
        }
        setTimeout(() => {
          setIsDialogOpen(false);
          setSelectedEvent(null);
          setSubmitStatus(null);
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  // reCAPTCHA invisible callback
  const onRecaptchaVerify = useCallback((token) => {
    submitFormWithToken(token);
  }, [submitFormWithToken]);

  const onRecaptchaExpired = useCallback(() => {
    // Token expired, will get new one on next submit
  }, []);

  // Render invisible reCAPTCHA when dialog opens
  useEffect(() => {
    if (isDialogOpen && recaptchaContainerRef.current && window.grecaptcha && window.grecaptcha.render) {
      if (recaptchaWidgetId.current !== null) {
        try {
          window.grecaptcha.reset(recaptchaWidgetId.current);
        } catch (e) {
          // Widget may have been removed
        }
      }
      
      setTimeout(() => {
        if (recaptchaContainerRef.current && recaptchaContainerRef.current.innerHTML === '') {
          try {
            recaptchaWidgetId.current = window.grecaptcha.render(recaptchaContainerRef.current, {
              sitekey: RECAPTCHA_SITE_KEY,
              size: 'invisible',
              callback: onRecaptchaVerify,
              'expired-callback': onRecaptchaExpired
            });
          } catch (e) {
            console.error('reCAPTCHA render error:', e);
          }
        }
      }, 100);
    }
  }, [isDialogOpen, onRecaptchaVerify, onRecaptchaExpired]);

  useEffect(() => {
    document.title = language === 'de' ? 'SourSweet | Reservierung' : 'SourSweet | Reservation';
  }, [language]);

  const isGerman = language === 'de';

  // Event cards data
  const eventCards = [
    {
      id: 'sour',
      name: 'Sour',
      description: isGerman ? 'Premium Cocktail-Service für Ihr Event' : 'Premium cocktail service for your event',
      image: '/images/sour-hero.jpg',
      minGuests: 15,
      duration: isGerman ? '2-5 Stunden' : '2-5 Hours',
      f1: isGerman ? 'Signature Cocktails' : 'Signature Cocktails',
      f2: isGerman ? 'Professionelle Barkeeper' : 'Professional Bartenders',
      f3: isGerman ? 'Premium Spirituosen' : 'Premium Spirits',
      f4: isGerman ? 'Elegante Präsentation' : 'Elegant Presentation'
    },
    {
      id: 'sweet',
      name: 'Sweet',
      description: isGerman ? 'Exquisite Desserts und süße Kreationen' : 'Exquisite desserts and sweet creations',
      image: '/images/sweet-hero.jpeg',
      minGuests: 15,
      duration: isGerman ? '2-4 Stunden' : '2-4 Hours',
      f1: isGerman ? 'Handgemachte Desserts' : 'Handmade Desserts',
      f2: isGerman ? 'Schokoladen-Kreationen' : 'Chocolate Creations',
      f3: isGerman ? 'Individuelle Gestaltung' : 'Custom Design',
      f4: isGerman ? 'Frische Zutaten' : 'Fresh Ingredients'
    },
    {
      id: 'foodtruck',
      name: isGerman ? 'Food Truck Buchung' : 'Food Truck Booking',
      description: isGerman ? 'Mobiler Service direkt zu Ihrem Veranstaltungsort' : 'Mobile service directly to your venue',
      image: '/images/event-foodtruck.webp',
      minGuests: 20,
      duration: isGerman ? '3-6 Stunden' : '3-6 Hours',
      f1: isGerman ? 'Mobiler Cocktail-Service' : 'Mobile Cocktail Service',
      f2: isGerman ? 'Komplettes Equipment' : 'Complete Equipment',
      f3: isGerman ? 'Flexibler Standort' : 'Flexible Location',
      f4: isGerman ? 'Event-Atmosphäre' : 'Event Atmosphere'
    },
    {
      id: 'birthday',
      name: isGerman ? 'Geburtstag' : 'Birthday',
      description: isGerman ? 'Unvergessliche Geburtstagsfeier mit Stil' : 'Unforgettable birthday celebration with style',
      image: '/images/event-geburtstag.webp',
      minGuests: 20,
      duration: isGerman ? '3-5 Stunden' : '3-5 Hours',
      f1: isGerman ? 'Geburtstagspakete' : 'Birthday Packages',
      f2: isGerman ? 'Thematische Dekoration' : 'Themed Decoration',
      f3: isGerman ? 'Individuelle Optionen' : 'Custom Options',
      f4: isGerman ? 'Party-Atmosphäre' : 'Party Atmosphere'
    },
    {
      id: 'garden',
      name: isGerman ? 'Garten Party' : 'Garden Party',
      description: isGerman ? 'Perfekte Begleitung für Ihre Outdoor-Feier' : 'Perfect accompaniment for your outdoor celebration',
      image: '/images/event-gartenparty.webp',
      minGuests: 20,
      duration: isGerman ? '3-6 Stunden' : '3-6 Hours',
      f1: isGerman ? 'Outdoor-Setup' : 'Outdoor Setup',
      f2: isGerman ? 'Kompletter Auf- und Abbau' : 'Complete Setup & Cleanup',
      f3: isGerman ? 'Getränke-Pairing' : 'Drink Pairing',
      f4: isGerman ? 'Garten-Atmosphäre' : 'Garden Atmosphere'
    },
    {
      id: 'dreams',
      name: isGerman ? 'Ihr Traum' : 'Your Dream',
      description: isGerman ? 'Teilen Sie uns Ihre Wünsche mit - wir machen es möglich' : 'Share your wishes - we make it possible',
      image: '/images/event-dream.webp',
      minGuests: 20,
      duration: isGerman ? 'Flexibel' : 'Flexible',
      f1: isGerman ? 'Personalisierte Planung' : 'Personalized Planning',
      f2: isGerman ? 'Flexible Zeiten' : 'Flexible Times',
      f3: isGerman ? 'Individuelle Themen' : 'Custom Themes',
      f4: isGerman ? 'Persönliche Beratung' : 'Personal Consultation',
      isSpecial: true
    }
  ];

  const handleEventSelect = (card) => {
    setSelectedEvent(card);
    setIsDialogOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const selectedDate = new Date(formData.date + 'T' + formData.time);
    const now = new Date();
    if (selectedDate < now) {
      setSubmitStatus('date-error');
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    // Execute invisible reCAPTCHA
    if (window.grecaptcha && recaptchaWidgetId.current !== null) {
      window.grecaptcha.execute(recaptchaWidgetId.current);
    }
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setSelectedEvent(null);
    setSubmitStatus(null);
  };

  const inputClasses = "w-full bg-white border border-lime-200 rounded-lg px-3 py-2 text-emerald-800 text-sm font-semibold placeholder-gray-400 focus:border-lime-500 focus:ring-1 focus:ring-lime-500/30 outline-none transition-all";
  const labelClasses = "block text-emerald-700 text-xs font-extrabold mb-1";

  return (
    <div className="page-transition" data-testid="reservation-page">
      {/* Hero Section */}
      <section className="relative py-16 sm:py-20 md:py-24 pt-24 sm:pt-28 md:pt-32 lg:pt-36 pb-20 sm:pb-32 md:pb-44 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center md:bg-fixed" style={{ backgroundImage: `url(${RESERVATION_HERO})` }}>
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/85 via-emerald-900/75 to-emerald-900/60" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="text-center max-w-xl mx-auto">
            <div className="flex items-center justify-center gap-3 mb-2 sm:mb-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-lime-400/20 flex items-center justify-center">
                <CalendarCheck className="text-lime-300" size={18} />
              </div>
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold text-white tracking-tight mb-2">
              {isGerman ? 'Reservierung' : 'Reservation'}
            </h1>
            <p className="text-xs sm:text-sm text-lime-100 font-semibold px-4">
              {isGerman 
                ? 'Wählen Sie Ihr perfektes Event-Erlebnis und lassen Sie uns Ihren besonderen Anlass unvergesslich machen.'
                : 'Choose your perfect event experience and let us make your special occasion unforgettable.'}
            </p>
          </div>
        </div>
      </section>

      {/* Event Cards Section */}
      <section className="py-8 bg-emerald-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Event Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {eventCards.map((card) => (
              <div
                key={card.id}
                className="rounded-2xl border border-emerald-600 bg-emerald-900 shadow-sm hover:shadow-[0_0_50px_rgba(253,224,71,0.8)] hover:border-yellow-300 transition-all duration-300 cursor-pointer group overflow-hidden"
                onClick={() => handleEventSelect(card)}
              >
                {/* Event Image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={card.image}
                    alt={card.name}
                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-emerald-900 via-transparent to-transparent" />
                </div>
                
                {/* Card Header */}
                <div className="text-center pb-4 border-b border-emerald-700 -mt-12 relative z-10 bg-emerald-900/95 pt-6 px-6">
                  <h3 className="text-2xl sm:text-3xl text-white mb-2 font-extrabold">{card.name}</h3>
                  <p className="text-sm text-lime-200 font-bold">{card.description}</p>
                </div>
                
                {/* Card Content */}
                <div className="p-6 space-y-4 bg-emerald-900">
                  {/* Features */}
                  <ul className="space-y-1.5">
                    <li className="flex items-center gap-2 text-lime-100 text-sm font-bold">
                      <span className="text-yellow-400">•</span>
                      {card.f1}
                    </li>
                    <li className="flex items-center gap-2 text-lime-100 text-sm font-bold">
                      <span className="text-yellow-400">•</span>
                      {card.f2}
                    </li>
                    <li className="flex items-center gap-2 text-lime-100 text-sm font-bold">
                      <span className="text-yellow-400">•</span>
                      {card.f3}
                    </li>
                    <li className="flex items-center gap-2 text-lime-100 text-sm font-bold">
                      <span className="text-yellow-400">•</span>
                      {card.f4}
                    </li>
                  </ul>
                  
                  {/* Info */}
                  <div className="flex items-center gap-6 text-lime-200 text-sm font-bold pt-2">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-yellow-400" />
                      <span>Min. {card.minGuests} {isGerman ? 'Gäste' : 'Guests'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-yellow-400" />
                      <span>{card.duration}</span>
                    </div>
                  </div>
                  
                  {/* CTA Button */}
                  <button
                    className="w-full mt-4 bg-yellow-300 hover:bg-yellow-200 text-emerald-950 border border-yellow-300 text-xs tracking-[0.15em] uppercase py-3 rounded-lg font-extrabold flex items-center justify-center gap-2 transition-all duration-300 hover:shadow-[0_0_25px_rgba(253,224,71,0.7)]"
                  >
                    {isGerman ? 'EVENT AUSWÄHLEN' : 'SELECT EVENT'}
                    <ChevronRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modal */}
      {isDialogOpen && (
        <>
          <div className="fixed inset-0 bg-black/60 z-[9999]" onClick={closeDialog} />
          <div 
            ref={dialogRef}
            className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
          >
            <div className="bg-white border border-lime-200 rounded-2xl p-4 sm:p-6 shadow-xl max-h-[85vh] overflow-y-auto w-full max-w-[600px]">
              {/* Header */}
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-xl font-bold text-emerald-800">
                    {isGerman ? 'Buchen' : 'Book'} {selectedEvent?.name}
                  </h2>
                  <p className="text-emerald-700 font-semibold text-xs mt-1">
                    {isGerman ? 'Füllen Sie Ihre Daten aus und wir melden uns in Kürze.' : 'Fill in your details and we will contact you shortly.'}
                  </p>
                </div>
                <button 
                  onClick={closeDialog}
                  className="text-emerald-600 hover:text-emerald-800 p-1 hover:bg-lime-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Status Messages */}
              {submitStatus === 'success' && (
                <div className="mb-4 p-3 bg-lime-100 border border-lime-300 rounded-lg text-lime-700 text-sm font-semibold">
                  {isGerman ? '✓ Anfrage erfolgreich gesendet!' : '✓ Request sent successfully!'}
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="mb-4 p-3 bg-red-100 border border-red-300 rounded-lg text-red-700 text-sm font-semibold">
                  {isGerman ? '✗ Fehler beim Senden. Bitte versuchen Sie es erneut.' : '✗ Error sending. Please try again.'}
                </div>
              )}
              {submitStatus === 'date-error' && (
                <div className="mb-4 p-3 bg-amber-100 border border-amber-300 rounded-lg text-amber-700 text-sm font-semibold">
                  {isGerman ? '⚠ Bitte wählen Sie ein zukünftiges Datum.' : '⚠ Please select a future date.'}
                </div>
              )}
              
              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelClasses}>{isGerman ? 'Name' : 'Name'} *</label>
                    <input type="text" name="name" placeholder="Max Mustermann" value={formData.name} onChange={handleInputChange} required className={inputClasses} />
                  </div>
                  <div>
                    <label className={labelClasses}>E-Mail *</label>
                    <input type="email" name="email" placeholder="max@beispiel.de" value={formData.email} onChange={handleInputChange} required className={inputClasses} />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelClasses}>{isGerman ? 'Telefon' : 'Phone'} *</label>
                    <input type="tel" name="phone" placeholder="+49 123 456789" value={formData.phone} onChange={handleInputChange} required className={inputClasses} />
                  </div>
                  <div>
                    <label className={labelClasses}>{isGerman ? 'Gäste' : 'Guests'} *</label>
                    <input type="number" name="guests" placeholder={`Min. ${selectedEvent?.minGuests || 15}`} min={selectedEvent?.minGuests || 15} value={formData.guests} onChange={handleInputChange} required className={inputClasses} />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelClasses}>{isGerman ? 'Datum' : 'Date'} *</label>
                    <input type="date" name="date" value={formData.date} onChange={handleInputChange} min={new Date().toISOString().split('T')[0]} required className={inputClasses} />
                  </div>
                  <div>
                    <label className={labelClasses}>{isGerman ? 'Uhrzeit' : 'Time'} *</label>
                    <input type="time" name="time" value={formData.time} onChange={handleInputChange} required className={inputClasses} />
                  </div>
                </div>
                
                <div>
                  <label className={labelClasses}>{isGerman ? 'Nachricht' : 'Message'}</label>
                  <textarea name="message" placeholder={isGerman ? 'Besondere Wünsche oder Fragen...' : 'Special requests or questions...'} value={formData.message} onChange={handleInputChange} rows={2} maxLength={1000} className={inputClasses} />
                </div>
                
                {/* Invisible reCAPTCHA container */}
                <div ref={recaptchaContainerRef}></div>

                <div className="flex items-center justify-between">
                  <p className="text-emerald-700 text-xs font-semibold">
                    {isGerman ? (
                      <>Mit dem Absenden akzeptieren Sie unsere <Link to="/datenschutz" className="text-lime-600 font-bold hover:underline">Datenschutzerklärung</Link>.</>
                    ) : (
                      <>By submitting you accept our <Link to="/privacy" className="text-lime-600 font-bold hover:underline">Privacy Policy</Link>.</>
                    )}
                  </p>
                  <a href={isGerman ? "https://www.google.com/recaptcha?hl=de" : "https://www.google.com/recaptcha?hl=en"} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center opacity-50 hover:opacity-80 transition-opacity">
                    <img src="https://www.gstatic.com/recaptcha/api2/logo_48.png" alt="reCAPTCHA" className="w-5 h-5" />
                    <span className="text-[7px] text-gray-500 font-medium">reCAPTCHA</span>
                  </a>
                </div>
                
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={closeDialog}
                    disabled={isSubmitting}
                    className="flex-1 flex items-center justify-center border border-lime-300 text-emerald-900 hover:bg-lime-50 hover:shadow-lg hover:shadow-orange-400/40 transition-all duration-300 rounded-lg py-3 font-extrabold text-sm"
                  >
                    {isGerman ? 'ABBRECHEN' : 'CANCEL'}
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 flex items-center justify-center gap-2 bg-lime-200 text-emerald-900 hover:bg-lime-300 hover:shadow-xl hover:shadow-orange-500/60 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 rounded-lg py-3 font-extrabold text-sm"
                  >
                    {isSubmitting ? (
                      <><Loader2 className="w-4 h-4 animate-spin" />{isGerman ? 'SENDEN...' : 'SENDING...'}</>
                    ) : (
                      <><Send className="w-4 h-4" />{isGerman ? 'ANFRAGE SENDEN' : 'SEND REQUEST'}</>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
